import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { Table, Spinner, Offcanvas, Card, Row, Col, Image } from 'react-bootstrap';
import { Box, Button, IconButton, InputBase, Paper, Tab, Tabs } from '@mui/material';
import { Formik, Form } from 'formik';
import { InputFormFiled } from '../../../components/Common/FormikField/InputFormFiled';
import { styled } from '@mui/material/styles';
import { GetSchema, user_name } from '../../../js/Base/ValidationSchema';
import toastAlert from '../../../js/Base/ToastAlert';
import SearchIcon from '@mui/icons-material/Search';
import CompanyOrgMemberTable from './CompanyOrgMemberTable';
import AddMemberToOrgModal from './AddMemberToOrgModal';
import {
  EditCompanyGroup,
  getCompanyGroupMembersByCompanyGroupId,
  getCompanyMembersByNoList,
  deleteCompanyGroupMemberApi
} from '../../../api/companyApi';
import { getAccountsByNoList } from '../../../api/accountApi';

const UpdateOrgDrawer = ({ groupsList, selectedGroup, isModalOpen, handleClose, submitEffect }) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [initialValues, setInitialValues] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const handleSearchChange = (e) => setInputValue(e.target.value);


  const OffcanvasContainer = useMemo(
    () =>
      styled(Offcanvas)(({ theme }) => ({
        zIndex: 1200,
      })),
    []
  );

  const handleTabChange = (event, newValue) => {
      setTabValue(newValue);
    };

  const handleCloseSidebar = () => {
    setShowSidebar(false);
  };

  useEffect(() => {
    if (isModalOpen) {
      setTabValue(0);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (selectedGroup) {
      setTempGroupMembers([]);
      setOrgMemberList([]);
      setInitialValues({
        orgName: selectedGroup.name || '',
        manager: selectedGroup.manager_data?.account?.name || '',
        parentOrg: selectedGroup.parent_groups_data?.uuid || null,
      });
      getCompanyMembersByCompanyGroup(selectedGroup.uuid);
    }
  }, [selectedGroup]);

  const formSchema = GetSchema({
    orgName: user_name(),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    try {
      const payload = {
        group_uuid: selectedGroup.uuid,
        name: values.orgName
      };
      const res = await EditCompanyGroup(payload);
      const data = res.data;

      if(data.success) {
        toastAlert.autoClose(true).showSuccess("編輯成功");
      } else {
        showErrorMessage(data?.code);
      }
    } catch (error) {
      toastAlert.autoClose(true).showError("編輯失敗");
    } finally {
      setSubmitting(false);
      handleClose();
      submitEffect();
    }
  };

  const showErrorMessage = (code) => {
    switch (code) {
      case "1":
        toastAlert.autoClose(true).showError("該部門存在子部門，必須先刪除子部門");
        break;
      case "2":
        toastAlert.autoClose(true).showError("最上層部門已經存在");
        break;
      case "3":
        toastAlert.autoClose(true).showError("最上層部門不可設定上層部門");
        break;
      default:
        toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
        break;
    }
  };

  // 組織成員
  const [tempGroupMembers, setTempGroupMembers] = useState([]);
  const [orgMemberList, setOrgMemberList] = useState([]);
  const [memberModalOpen, setMemberModalOpen] = useState(false);

  const col_name = [
    "姓名", "電子信箱", "功能",
  ];
  const data_frame = [
    { keyName: 'company_member_data.account.name' },
    { keyName: 'company_member_data.account.email' },
    { keyName: 'more', isClick: true },
  ];

  const handleMemberModalClose = () => {
    setMemberModalOpen(false);
  };

  const getCompanyMembersByCompanyGroup = async (groupUuid) => {
    if (!groupUuid) {
      toastAlert.autoClose(true).showError("查無資料，請聯絡系統管理員");
      return;
    }

    try {
      setLoading(true);
      const response = await getCompanyGroupMembersByCompanyGroupId(groupUuid);
      const data = response.data;

      if (data.success) {
        setTempGroupMembers(data.members);
        const memberUuidList = data.members.map((groupMember) => groupMember.company_member_uuid);
        await getCompanyMembersByNoListsByWebApi(data.members, memberUuidList);
      } else {
        toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
    } finally {
      setLoading(false);
    }
  };


  const getCompanyMembersByNoListsByWebApi = async (groupMembers, memberUuidList) => {
    if (!memberUuidList || memberUuidList.length === 0) return;

    try {
      setLoading(true);
      const memberRes = await getCompanyMembersByNoList(memberUuidList);
      const compMembers = memberRes.data.members || [];

      // company_member_data
      const newGroupMembers = groupMembers.map((gm) => ({
        ...gm,
        company_member_data:
        compMembers.find((m) => (gm.company_member_uuid === m.uuid) || null)
      }));

      setTempGroupMembers(newGroupMembers);
      
      const acctUuidList = newGroupMembers
        .map((m) => m.company_member_data?.acct_uuid)
        .filter(Boolean);

      if (acctUuidList.length === 0) {
        setOrgMemberList(newGroupMembers);
        return;
      }      
      const acctRes = await getAccountsByNoList(acctUuidList);
      const accounts = acctRes.data.accounts || [];

      let newGroupMembersAcct = JSON.parse(JSON.stringify(newGroupMembers));
      for (let i = 0; i < newGroupMembersAcct.length; i++) {
        const cm = newGroupMembersAcct[i].company_member_data;
        newGroupMembersAcct[i].company_member_data.account =
          accounts.find((account) => account.uuid === cm.acct_uuid) || null;
      }

      setOrgMemberList(newGroupMembersAcct);

    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
    } finally {
      setLoading(false);
    }
  };

  const handleSetAdmin = async (groupMemberNo) => {
    try {
      const groupMember = orgMemberList.filter((groupMember) => groupMember.uuid === groupMemberNo)[0];
      const payload = {
        group_uuid: selectedGroup.uuid,
        manager_uuid: groupMember.company_member_uuid
      };
      const res = await EditCompanyGroup(payload);
      const data = res.data;

      if(data.success) {
        toastAlert.autoClose(true).showSuccess("編輯成功");
      } else {
        showErrorMessage(data?.code);
      }
    } catch (error) {
      toastAlert.autoClose(true).showError("編輯失敗");
    } finally {
      handleClose();
      submitEffect();
    }
  };

  const handleDelete = async (groupMemberUuid) => {
    if (!groupMemberUuid) return;
    try {
      const response = await deleteCompanyGroupMemberApi(groupMemberUuid);
      const data = response.data;

      if (data.success) {
        toastAlert.autoClose(true).showInfo('刪除成功');
        getCompanyMembersByCompanyGroup(selectedGroup.uuid);
      } else {
        toastAlert.autoClose(true).showError('刪除失敗');
      }
    } catch (error) {
      toastAlert.autoClose(true).showError('刪除失敗，請稍後再試');
    }
  };



  return (
    <OffcanvasContainer
      className="CompOrgManagement Drawer"
      show={isModalOpen}
      onHide={handleClose}
      placement={'end'}
      style={{ width: '52%' }}
    >
      <Fragment>
        <Offcanvas.Header className="offcanvas-header" closeButton>
          <Offcanvas.Title className="text-title">詳細資訊</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="offcanvas-body">
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{
                  'button.Mui-selected': { color: '#7FBE25' },
                }}
                TabIndicatorProps={{ style: { backgroundColor: '#7FBE25' } }}
                className="tabs"
              >
                <Tab label="組織資訊" />
                <Tab label="組織成員" />
              </Tabs>
            </Box>
          </Box>

          {tabValue === 0 && (
            <Row className="tab-2">
              <Col md={12} style={{ height: '70vh' }}>
                <Formik
                  initialValues={initialValues}
                  onSubmit={handleSubmit}
                  validationSchema={formSchema}
                  enableReinitialize={true}
                >
                  {({ errors, touched, isSubmitting }) => (
                    <Form className="form">
                      <div style={{ height: '85%' }}>
                        <InputFormFiled
                          title="組織名稱"
                          name="orgName"
                          type="name"
                          invalid={errors.orgName && touched.orgName}
                        />
                        <InputFormFiled
                          title="主管"
                          name="manager"
                          type="name"
                          isReadOnly={true}
                          disabled={true}
                          invalid={errors.manager && touched.manager}
                        />
                        <InputFormFiled
                          options={groupsList}
                          title="上層組織"
                          name="parentOrg"
                          type="select"
                          isReadOnly={true}
                          disabled={true}
                          orgOption={true}
                          invalid={errors.parentOrg && touched.parentOrg}
                        />
                      </div>
                      <hr />
                      <div className="offcanvas-footer" style={{ height: '15%' }}>
                        <Button type="submit" className="btn btn-custom" disabled={isSubmitting}>
                          確認
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </Col>
            </Row>
          )}
          {tabValue === 1 && (
            <Fragment>
              <Row className="tab-1">
                <Col md={5}>
                  <Paper
                    variant="outlined"
                    component="form"
                    sx={{ p: '2px 2px', display: 'flex', alignItems: 'left', height: '32px' }}
                  >
                    <InputBase
                      sx={{ flex: 1, p: '10px' }}
                      style={{ fontSize: '12px', boxShadow: 'none' }}
                      placeholder="請輸入姓名或帳號"
                      inputProps={{ 'aria-label': 'search keyword' }}
                      value={inputValue}
                      onChange={handleSearchChange}
                    />
                    <IconButton type="button" sx={{ p: '12px' }} aria-label="search">
                      <SearchIcon style={{ color: '#E2E2E2' }} />
                    </IconButton>
                  </Paper>
                </Col>
                <Col md={4} />
                <Col md={3} style={{ justifySelf: 'flex-end' }}>
                  <Button className="btn-secondary btn-org-member"
                  onClick={() => setMemberModalOpen(true)}
                  >
                    新增組織成員
                  </Button>
                </Col>

                {loading ? (
                  <div className="d-flex justify-content-center my-3">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Col md={12} style={{ marginTop: '20px' }}>
                    <CompanyOrgMemberTable
                      rowData={orgMemberList.filter(
                        (m) =>
                          m.company_member_data?.account?.name?.indexOf(inputValue) > -1 ||
                          m.company_member_data?.account?.email?.indexOf(inputValue) > -1
                      )}
                      colName={col_name}
                      dataFrame={data_frame}
                      delFunc={handleDelete}
                      editModalOpen={handleSetAdmin}
                    />
                  </Col>
                )}
              </Row>
            </Fragment>
          )}
        </Offcanvas.Body>
      </Fragment>

      <AddMemberToOrgModal
        isModalOpen={memberModalOpen}
        handleClose={handleMemberModalClose}
        selectedGroup={selectedGroup}
        submitEffect={() => {
          if (selectedGroup?.uuid) {
            getCompanyMembersByCompanyGroup(selectedGroup.uuid);
          }
        }}
      />
    </OffcanvasContainer>
  );
}

export default UpdateOrgDrawer;
